import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

interface MonthData {
    value: number;
    displayName: string;
    disabled: boolean;
    selected: boolean;
}

@Component({
    selector: 'app-date-picker-custom-header',
    templateUrl: './date-picker-custom-header.component.html',
    styleUrls: ['./date-picker-custom-header.component.css'],
    encapsulation: ViewEncapsulation.None // This allows us to affect calendar cells
})
export class DatePickerCustomHeaderComponent<D> implements OnInit, OnDestroy {
    private readonly _destroyed = new Subject<void>();

    // Configuration
    private readonly YEARS_TO_SHOW = 20;

    // State
    years: number[] = [];
    months: MonthData[] = [];
    currentDate = new Date();
    showSelectionPanel = false;
    activeTab: 'month' | 'year' = 'month';

    private selectedDates: D[] = [];

    // Month names in English
    private readonly MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    constructor(
        private _calendar: MatCalendar<D>,
        private _dateAdapter: DateAdapter<D>,
        @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
        private cdr: ChangeDetectorRef,
        public translateService: TranslateService
    ) {
        // Track changes in the calendar state
        _calendar.stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => {
            this.updateMonthData();
            this.updateSelectedDates();
            this.cdr.markForCheck();
        });

        // Listen for date selection in the calendar
        _calendar.selectedChange.pipe(takeUntil(this._destroyed)).subscribe(date => {
            if (date) {
                this.updateSelectedDates();
                this.cdr.markForCheck();
            }
        });
    }

    ngOnInit(): void {
        this.generateYearsList();
        this.updateMonthData();
        this.updateSelectedDates();
    }

    // Update our stored selected dates from the calendar
    private updateSelectedDates(): void {
        this.selectedDates = [];

        // Handle single selection
        if (this._calendar.selected) {
            if (Array.isArray(this._calendar.selected)) {
                this.selectedDates = [...this._calendar.selected];
            } else {
                this.selectedDates = [this._calendar.selected as D];
            }
        }

        // After updating selected dates, ensure they're highlighted
        setTimeout(() => {
            this.highlightSelectedDates();
        }, 0);
    }

    // Manually highlight the selected date cells in the DOM
    private highlightSelectedDates(): void {
        if (!this.selectedDates.length) return;

        // Find all date cells
        const dateCells = document.querySelectorAll('.mat-calendar-body-cell');

        // Remove any previous highlights
        dateCells.forEach(cell => {
            cell.classList.remove('selected-date');
        });

        // Add highlight class to selected dates
        dateCells.forEach(cell => {
            const dateAttr = cell.getAttribute('aria-label');
            if (!dateAttr) return;

            for (const selectedDate of this.selectedDates) {
                const formattedDate = this._dateAdapter.format(selectedDate, this._dateFormats.display.dateA11yLabel);
                if (dateAttr.includes(formattedDate)) {
                    cell.classList.add('selected-date');

                    // Also apply style to the cell content to ensure visibility
                    const cellContent = cell.querySelector('.mat-calendar-body-cell-content');
                    if (cellContent) {
                        cellContent.classList.add('selected-date-content');
                    }
                }
            }
        });
    }

    private generateYearsList(): void {
        const currentYear = this.currentDate.getFullYear();
        const startYear = currentYear - this.YEARS_TO_SHOW;
        const endYear = currentYear;

        this.years = [];

        for (let year = endYear; year >= startYear; year--) {
            this.years.push(year);
        }
    }

    private updateMonthData(): void {
        const currentMonth = this.getCurrentMonth();
        const currentYear = this.getCurrentYear();
        const isCurrentYear = currentYear === this.currentDate.getFullYear();

        // Always use English month names regardless of language
        this.months = this.MONTH_NAMES.map((name, index) => ({
            value: index,
            displayName: name,
            disabled: isCurrentYear && index > this.currentDate.getMonth(),
            selected: index === currentMonth
        }));
    }

    isRtlLanguage(): boolean {
        return this.translateService.currentLang === 'ar';
    }

    getCurrentYear(): number {
        return this._dateAdapter.getYear(this._calendar.activeDate);
    }

    getCurrentMonth(): number {
        return this._dateAdapter.getMonth(this._calendar.activeDate);
    }

    selectYear(year: number): void {
        const activeDate = this._calendar.activeDate;
        const month = this._dateAdapter.getMonth(activeDate);
        const day = this._dateAdapter.getDate(activeDate);

        this._calendar.activeDate = this._dateAdapter.createDate(year, month, day);
        this.closeSelectionPanel();
        this.updateMonthData();

        // Force a re-render of the calendar view to show the selected dates in the new year
        setTimeout(() => {
            this.highlightSelectedDates();
            this._calendar.focusActiveCell(); // Helps refresh the calendar
            this.cdr.markForCheck();
        }, 10);
    }

    selectMonth(monthIndex: number): void {
        const activeDate = this._calendar.activeDate;
        const year = this._dateAdapter.getYear(activeDate);
        const day = this._dateAdapter.getDate(activeDate);

        let adjustedDay = day;
        if (monthIndex === 1) {
            // Check if the day is beyond what February can have
            const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
            const maxDayInFeb = isLeapYear ? 29 : 28;

            if (day > maxDayInFeb) {
                adjustedDay = maxDayInFeb;
            }
        }

        this._calendar.activeDate = this._dateAdapter.createDate(year, monthIndex, adjustedDay);
        this.closeSelectionPanel();

        // Force a re-render of the calendar view to show the selected dates in the new month
        setTimeout(() => {
            this.highlightSelectedDates();
            this._calendar.focusActiveCell(); // Helps refresh the calendar
            this.cdr.markForCheck();
        }, 10);
    }

    isYearDisabled(year: number): boolean {
        return year > this.currentDate.getFullYear();
    }

    get periodLabel(): string {
        return this._dateAdapter.format(this._calendar.activeDate, this._dateFormats.display.monthYearLabel);
    }

    previousClicked(mode: 'month' | 'year'): void {
        this._calendar.activeDate =
            mode === 'month'
                ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
                : this._dateAdapter.addCalendarYears(this._calendar.activeDate, -1);

        // After navigation, ensure selected dates remain highlighted
        setTimeout(() => {
            this.highlightSelectedDates();
            this.cdr.markForCheck();
        }, 10);
    }

    nextClicked(mode: 'month' | 'year'): void {
        const activeDate = this._calendar.activeDate;
        const activeYear = this._dateAdapter.getYear(activeDate);
        const activeMonth = this._dateAdapter.getMonth(activeDate);
        const currentYear = this.currentDate.getFullYear();
        const currentMonth = this.currentDate.getMonth();

        if (mode === 'month') {
            if (activeYear < currentYear || (activeYear === currentYear && activeMonth < currentMonth)) {
                this._calendar.activeDate = this._dateAdapter.addCalendarMonths(activeDate, 1);
            }
        } else if (activeYear < currentYear) {
            this._calendar.activeDate = this._dateAdapter.addCalendarYears(activeDate, 1);
        }

        // After navigation, ensure selected dates remain highlighted
        setTimeout(() => {
            this.highlightSelectedDates();
            this.cdr.markForCheck();
        }, 10);
    }

    toggleSelectionPanel(): void {
        this.showSelectionPanel = !this.showSelectionPanel;
    }

    closeSelectionPanel(): void {
        this.showSelectionPanel = false;
        setTimeout(() => {
            this.highlightSelectedDates();
            this._calendar.focusActiveCell();
            this.cdr.markForCheck();
        }, 10);
    }

    setActiveTab(tab: 'month' | 'year'): void {
        this.activeTab = tab;
    }

    ngOnDestroy(): void {
        this._destroyed.next();
        this._destroyed.complete();
    }
}
